module Client.IllyAdmin.Actions.TokenRevocationList

open System
open Shared
open Client
open Elmish
open Fable.React
open Fable.React.Props
open Illy.SafeStack
open Illy.SafeStack.Client
open Client.IllyAdmin.Actions.Types

type NoButtons = NoButtons

type Model =
    {
        Loading: bool
        GenerateHashForToken: Text.Model
        TokenHash: string option
        GeneratingTokenHash: bool
        Data: Grid.Model<string * DateTime, NoButtons>
    }

type Msg =
    | GenerateHashForToken 
    | TokenHashLoaded of string
    | GenerateHashForTokenMsg of Text.Msg
    | DataLoaded of list<string * DateTime>
    | GridMsg of Grid.Msg<NoButtons>
    | ApiError of ApiError

module Commands =
    let getTokenHash token =
        Api.secureCall<IRevocationIllyAdminApi,_,_,_> SecuredRoute.IllyAdmin (fun api -> api.GetTokenHash) token TokenHashLoaded ApiError

    let getData() =
        Api.secureCall<IRevocationIllyAdminApi,_,_,_> SecuredRoute.IllyAdmin (fun api -> api.GetEntries) () DataLoaded ApiError

module Init =
    let generateHashForToken token =
        {Text.CustomSettings.defaultOptions() with
            TextType =
                Text.TextAreaSettings.defaultOptions()
                |> Text.TextType.TextArea
        }
        |> Text.init "GenerateHashForToken" "Generate Hash For Token" (Some "Enter a JWT token") 10000. false token 

let init () =
    {
        Loading = true
        GenerateHashForToken = Init.generateHashForToken None
        TokenHash = None
        GeneratingTokenHash = false
        Data = Grid.init "hash" "hashes" "There are no revoked hashes" []
    }, Commands.getData()

let update msg (model: Model) =
    match msg with
    | GenerateHashForTokenMsg tMsg ->
        let m, c = Text.update tMsg model.GenerateHashForToken
        {model with GenerateHashForToken = m}, Cmd.map GenerateHashForTokenMsg c |> SideEffect.Cmd
    | GenerateHashForToken ->
        let token = Text.toString model.GenerateHashForToken |> Option.defaultValue ""
        if String.IsNullOrWhiteSpace token then
            {model with
                GenerateHashForToken = Text.setValidations model.GenerateHashForToken (Some "Must be set") None
            }, SideEffect.none
        else 
            {model with GeneratingTokenHash = true}, Commands.getTokenHash (token.Trim()) |> SideEffect.Cmd
    | TokenHashLoaded hash ->
        {model with
            TokenHash = Some hash
            GenerateHashForToken = Text.resetValidations model.GenerateHashForToken
            GeneratingTokenHash = false 
        }, SideEffect.none
    | DataLoaded data ->
        {model with
            Loading = false
            Data = model.Data |> Grid.updateData data data.Length
        }, SideEffect.none
    | ApiError err ->
        {model with
            Loading = false
            GeneratingTokenHash = false
        }, err |> AppError.ApiError |> ActionEvent.AppError |> SideEffect.Event
    | GridMsg gmsg ->
        let gmod, gcmd = Grid.update gmsg model.Data
        match gcmd with
        | SideEffect.Cmd cmd ->
            { model with Data = gmod }, Cmd.map GridMsg cmd |> SideEffect.Cmd
        | SideEffect.Event _ ->
            model, SideEffect.none

let view model dispatch =
    [
        if model.Loading then yield jQueryUI.spinner
        else
            yield section [] [
                Text.text model.Loading model.GenerateHashForToken (GenerateHashForTokenMsg >> dispatch)
                if model.GeneratingTokenHash then jQueryUI.saveSpinner else jQueryUI.button "Generate" GenerateHashForToken dispatch
                match model.TokenHash with
                | None -> ()
                | Some x ->
                    span [] [
                        str " ===> "
                        b [] [ str "Generated hash: "]
                        str x
                    ]
            ]

            yield jQueryUI.illy_info [ str "A list of the hashes revoked currently in the system." ]
            yield! Grid.view
                model.Data
                [
                    jQueryUI.ColumnMetaData.createWithHeader [ str "Hash" ] (fst >> str >> List.singleton)
                    jQueryUI.ColumnMetaData.createWithHeader [ str "Date" ] (snd >> illyDateTime >> str >> List.singleton)
                ]
                model.Loading
                (GridMsg >> dispatch)
                
    ]

let metaData =
    AdminActionMeta.create
        AdminActionKey.ViewRevocations
        "View Revoked Login Hashes"
        "A basic list of any JWT that has been revoked."
        ActionGroup.Caches